import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GroupsIcon from '@mui/icons-material/Groups';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BlockIcon from '@mui/icons-material/Block';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useRef, useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  SeoTrackedKeyword,
  SeoKeywordSnapshot,
  SeoPage,
  SeoTask,
  SeoCopilotCycle,
  SeoAhrefsDomainSnapshot,
  SeoAhrefsKeywordCache,
} from '@/types/api/seo';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import LinkIcon from '@mui/icons-material/Link';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SerpCompetitorAnalyzer from '@/components/SerpCompetitorAnalyzer';
import InternalLinkFinder from '@/components/InternalLinkFinder';
import KeywordOpportunities from '@/components/KeywordOpportunities';
import ChangeImpactTimeline from '@/components/ChangeImpactTimeline';
import KeywordHistoryChart from '@/components/KeywordHistoryChart';
import KeywordTaskList from '@/components/KeywordTaskList';
import {
  SerpAnalysisResult,
  SerpSummary,
  ContentOptimizationResult,
  InternalLinkFinderResult,
  LivePositionResult,
} from '@/services/seoAi';
import { WpAppliedChange } from '@/types/api/seo';

type GSCKeywordData = {
  dailyTrend: Array<{
    date: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>;
  rankingPages: Array<{
    page: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>;
  totals: { clicks: number; impressions: number; avgCtr: number; avgPosition: number };
};

type Props = {
  keyword: SeoTrackedKeyword | null;
  snapshots: SeoKeywordSnapshot[];
  gscData: GSCKeywordData | null;
  isLoading: boolean;
  isLoadingGSC: boolean;
  notFound: boolean;
  days: number;
  onDaysChange: (days: number) => void;
  // AI features
  projectId: string;
  keywordId: string;
  competitorUrls: string[];
  userSiteUrl?: string;
  serpAnalysis: SerpAnalysisResult | null;
  serpSummary: SerpSummary | null;
  contentOptimization: ContentOptimizationResult | null;
  internalLinks: InternalLinkFinderResult | null;
  onSerpAnalysisComplete: (result: SerpAnalysisResult) => void;
  sitePages: Array<{ page: string; keyword?: string }>;
  // Target page
  targetPageUrl: string;
  projectPages: SeoPage[];
  onTargetPageChange: (pageId: string | null, pageUrl: string) => void;
  // Impact tracking
  appliedChanges: WpAppliedChange[];
  // Live position
  livePosition: LivePositionResult | null;
  livePositionLoading: boolean;
  // Tasks
  tasks: SeoTask[];
  tasksLoading: boolean;
  onTaskStatusChange: (taskId: string, status: SeoTask['status']) => Promise<void>;
  onTaskApplyToWp: (task: SeoTask) => Promise<void>;
  onTaskCreated: () => void;
  wpConnected: boolean;
  // Copilot
  copilotSession: {
    id: string;
    status: string;
    totalCycles: number;
    currentPosition: number | null;
    startingPosition: number | null;
    bestPosition: number | null;
    targetPosition: number;
    maxCycles: number;
    nextCycleAt: string | null;
  } | null;
  copilotCycles: SeoCopilotCycle[];
  onActivateCopilot: () => void;
  onPauseCopilot: () => void;
  onResumeCopilot: () => void;
  onRerunCopilotAnalysis: () => void;
  isRerunning: boolean;
  // Ahrefs
  ahrefsKd: SeoAhrefsKeywordCache | null;
  ahrefsDomainSnapshot: SeoAhrefsDomainSnapshot | null;
  ahrefsNewBacklinks: number;
  ahrefsLostBacklinks: number;
};

const fmtNum = (n: number): string => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

const positionColor = (pos: number | null | undefined): string => {
  if (pos == null) return '#96979b';
  if (pos <= 3) return '#0D9D58';
  if (pos <= 10) return '#4285F4';
  if (pos <= 20) return '#F4B400';
  return '#EA4335';
};

const formatPath = (url: string): string => {
  try {
    const u = new URL(url);
    return u.pathname + (u.search || '');
  } catch {
    return url.replace(/^https?:\/\/[^/]+/, '') || '/';
  }
};

const cardSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  p: 2.5,
  backgroundColor: '#fff',
  textAlign: 'center' as const,
  flex: 1,
  minWidth: 140,
};

const sectionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  p: 3,
  backgroundColor: '#fff',
  mb: 4,
};

// Expandable cycle card with tasks and applied changes
function CycleCard({
  cycle,
  cycleTasks,
  cycleChanges,
  impactColor,
  posGain,
}: {
  cycle: SeoCopilotCycle;
  cycleTasks: SeoTask[];
  cycleChanges: WpAppliedChange[];
  impactColor: string;
  posGain: number | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasContent = cycleTasks.length > 0 || cycleChanges.length > 0;

  const taskStatusIcon = (status: string) => {
    if (status === 'done')
      return <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#0D9D58' }} />;
    if (status === 'dismissed') return <BlockIcon sx={{ fontSize: 14, color: '#71717A' }} />;
    return <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: '#054AD9' }} />;
  };

  const taskTypeLabel = (type: string) => type.replace(/_/g, ' ');

  return (
    <Box
      sx={{
        borderRadius: '8px',
        backgroundColor: '#fff',
        border: '1px solid',
        borderColor: 'hsla(0,0%,0%,0.08)',
        overflow: 'hidden',
      }}
    >
      {/* Clickable cycle header */}
      <Box
        onClick={() => hasContent && setExpanded(!expanded)}
        sx={{
          p: 1.5,
          cursor: hasContent ? 'pointer' : 'default',
          '&:hover': hasContent ? { backgroundColor: 'hsla(0,0%,0%,0.02)' } : {},
          '&:active': hasContent ? { backgroundColor: 'hsla(0,0%,0%,0.04)' } : {},
          transition: 'background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              backgroundColor: '#054AD9',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {cycle.cycleNumber}
          </Box>
          <Typography variant="caption" fontWeight={500} sx={{ fontSize: 12 }}>
            Cycle {cycle.cycleNumber}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            {new Date(cycle.createdAt).toLocaleDateString()}
          </Typography>

          {/* Task count badge */}
          {hasContent && (
            <Chip
              label={`${cycleTasks.length} task${cycleTasks.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{ fontSize: 9, height: 16, fontWeight: 500 }}
              variant="outlined"
            />
          )}

          {/* Position change */}
          {cycle.positionBefore != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
              <Typography variant="caption" sx={{ fontSize: 11 }}>
                #{cycle.positionBefore.toFixed(1)}
              </Typography>
              {cycle.positionAfter != null && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                    &rarr;
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ fontSize: 11, color: positionColor(cycle.positionAfter) }}
                  >
                    #{cycle.positionAfter.toFixed(1)}
                  </Typography>
                  {posGain != null && posGain !== 0 && (
                    <Chip
                      label={`${posGain > 0 ? '+' : ''}${posGain.toFixed(1)}`}
                      size="small"
                      sx={{
                        fontSize: 9,
                        height: 16,
                        fontWeight: 700,
                        color: '#fff',
                        backgroundColor: posGain > 0 ? '#0D9D58' : '#EA4335',
                      }}
                    />
                  )}
                </>
              )}
              {cycle.positionAfter == null && cycle.impactStatus === 'pending' && (
                <Chip
                  label="pending"
                  size="small"
                  sx={{ fontSize: 9, height: 16, color: '#71717A' }}
                />
              )}

              {/* Expand icon */}
              {hasContent &&
                (expanded ? (
                  <ExpandLessIcon sx={{ fontSize: 16, color: '#71717A', ml: 0.5 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 16, color: '#71717A', ml: 0.5 }} />
                ))}
            </Box>
          )}

          {/* Expand icon when no position data */}
          {cycle.positionBefore == null && hasContent && (
            <Box sx={{ ml: 'auto' }}>
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: 16, color: '#71717A' }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16, color: '#71717A' }} />
              )}
            </Box>
          )}
        </Box>

        {/* Diagnosis */}
        {cycle.diagnosisReasoning && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: 11, display: 'block', mb: 0.5 }}
          >
            {cycle.diagnosisReasoning}
          </Typography>
        )}

        {/* Action taken */}
        {cycle.actionTaken && cycle.actionTaken !== 'none' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
            <Chip
              label={cycle.actionTaken.replace('_', ' ')}
              size="small"
              sx={{ fontSize: 9, height: 16, textTransform: 'capitalize' }}
              variant="outlined"
            />
            {cycle.actionDescription && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: 10,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {cycle.actionDescription
                  .replace(/^Applied "[^"]+" to WordPress\. Value: /, '')
                  .slice(0, 120)}
              </Typography>
            )}
          </Box>
        )}

        {/* Error */}
        {cycle.errorMessage && (
          <Typography
            variant="caption"
            color="error"
            sx={{ fontSize: 10, mt: 0.5, display: 'block' }}
          >
            {cycle.errorMessage}
          </Typography>
        )}

        {/* Impact badge */}
        {cycle.impactStatus && cycle.impactStatus !== 'pending' && (
          <Box sx={{ mt: 0.75 }}>
            <Chip
              label={
                cycle.impactStatus === 'improved'
                  ? 'Position improved'
                  : cycle.impactStatus === 'declined'
                  ? 'Position declined'
                  : 'No change'
              }
              size="small"
              sx={{ fontSize: 9, height: 16, color: impactColor, borderColor: impactColor }}
              variant="outlined"
            />
          </Box>
        )}
      </Box>

      {/* Expandable details: tasks + applied changes */}
      {expanded && hasContent && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'hsla(0,0%,0%,0.08)',
            backgroundColor: '#FAFAFA',
            px: 1.5,
            py: 1,
          }}
        >
          {/* Tasks */}
          {cycleTasks.length > 0 && (
            <>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ fontSize: 9, letterSpacing: 0.5, display: 'block', mb: 0.75 }}
              >
                TASKS
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  mb: cycleChanges.length > 0 ? 1.5 : 0,
                }}
              >
                {cycleTasks.map((task) => {
                  // Match WP change for before/after display on done tasks
                  const matchedChange =
                    task.status === 'done' && task.appliedToWp
                      ? cycleChanges.find((c) => c.changeType === task.taskType)
                      : null;

                  return (
                    <Box
                      key={task.id}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.25 }}
                    >
                      {taskStatusIcon(task.status)}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography variant="caption" fontWeight={500} sx={{ fontSize: 11 }}>
                            {task.title.replace('[Copilot] ', '')}
                          </Typography>
                          <Chip
                            label={taskTypeLabel(task.taskType)}
                            size="small"
                            sx={{ fontSize: 8, height: 14, textTransform: 'capitalize' }}
                            variant="outlined"
                          />
                          <Chip
                            label={
                              task.status === 'done'
                                ? task.appliedToWp
                                  ? 'Applied to WP'
                                  : 'Done'
                                : task.status.replace('_', ' ')
                            }
                            size="small"
                            sx={{
                              fontSize: 8,
                              height: 14,
                              color:
                                task.status === 'done'
                                  ? '#0D9D58'
                                  : task.status === 'dismissed'
                                  ? '#71717A'
                                  : '#054AD9',
                              borderColor:
                                task.status === 'done'
                                  ? '#0D9D58'
                                  : task.status === 'dismissed'
                                  ? '#71717A'
                                  : '#054AD9',
                            }}
                            variant="outlined"
                          />
                        </Box>

                        {/* Before/After for applied tasks */}
                        {matchedChange ? (
                          <Box
                            sx={{
                              mt: 0.5,
                              borderRadius: '6px',
                              backgroundColor: '#F4F4F5',
                              p: 0.75,
                            }}
                          >
                            {matchedChange.previousValue && (
                              <Box sx={{ display: 'flex', gap: 0.5, mb: 0.25 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    minWidth: 38,
                                    color: '#71717A',
                                    flexShrink: 0,
                                  }}
                                >
                                  Before
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: 10,
                                    color: '#71717A',
                                    textDecoration: 'line-through',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {matchedChange.previousValue.length > 200
                                    ? matchedChange.previousValue.slice(0, 200) + '...'
                                    : matchedChange.previousValue}
                                </Typography>
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  minWidth: 38,
                                  color: '#0D9D58',
                                  flexShrink: 0,
                                }}
                              >
                                After
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontSize: 10, color: '#0D9D58', wordBreak: 'break-word' }}
                              >
                                {matchedChange.newValue.length > 200
                                  ? matchedChange.newValue.slice(0, 200) + '...'
                                  : matchedChange.newValue}
                              </Typography>
                            </Box>
                          </Box>
                        ) : task.suggestedValue ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: 10,
                              mt: 0.25,
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}
                          >
                            {task.suggestedValue.slice(0, 150)}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {/* Applied WordPress changes */}
          {cycleChanges.length > 0 && (
            <>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ fontSize: 9, letterSpacing: 0.5, display: 'block', mb: 0.75 }}
              >
                APPLIED CHANGES
              </Typography>
              <ChangeImpactTimeline changes={cycleChanges} compact />
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

const SeoKeywordDetailComponent = function ({
  keyword,
  snapshots,
  gscData,
  isLoading,
  isLoadingGSC,
  notFound,
  days,
  onDaysChange,
  projectId,
  keywordId,
  competitorUrls,
  userSiteUrl,
  serpAnalysis,
  serpSummary,
  contentOptimization,
  internalLinks,
  onSerpAnalysisComplete,
  sitePages,
  targetPageUrl,
  projectPages,
  onTargetPageChange,
  appliedChanges,
  livePosition,
  livePositionLoading,
  tasks,
  tasksLoading,
  onTaskStatusChange,
  onTaskApplyToWp,
  onTaskCreated,
  wpConnected,
  copilotSession,
  copilotCycles,
  onActivateCopilot,
  onPauseCopilot,
  onResumeCopilot,
  onRerunCopilotAnalysis,
  isRerunning,
  ahrefsKd,
  ahrefsDomainSnapshot,
  ahrefsNewBacklinks,
  ahrefsLostBacklinks,
}: Props): JSX.Element {
  const basePath = `/seox/projects/${projectId}`;
  const reanalyzeRef = useRef<(() => void) | null>(null);
  const handleReanalyzeRef = useCallback((fn: (() => void) | null) => {
    reanalyzeRef.current = fn;
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#054AD9' }} />
      </Box>
    );
  }

  if (notFound || !keyword) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Keyword not found.
        </Typography>
      </Box>
    );
  }

  const latest = keyword.latestSnapshot;
  const totals = gscData?.totals;
  const dailyTrend = gscData?.dailyTrend ?? [];
  const rankingPages = gscData?.rankingPages ?? [];

  // Position change from snapshots
  const posChange =
    latest?.previousPosition != null && latest?.position != null
      ? latest.previousPosition - latest.position
      : null;

  const rankingPageUrl = targetPageUrl || latest?.url || rankingPages[0]?.page || '';

  return (
    <Box sx={{}}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h5" fontWeight={700}>
            {keyword.keyword}
          </Typography>
          {livePosition?.livePosition != null ? (
            <Chip
              label={`#${livePosition.livePosition} live`}
              size="small"
              sx={{
                fontWeight: 500,
                color: '#fff',
                backgroundColor: positionColor(livePosition.livePosition),
              }}
            />
          ) : (
            <Chip
              label={latest?.position != null ? `#${latest.position.toFixed(1)}` : 'Not ranked'}
              size="small"
              sx={{
                fontWeight: 500,
                color: '#fff',
                backgroundColor: positionColor(latest?.position),
              }}
            />
          )}
          {posChange != null && posChange !== 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                color: posChange > 0 ? 'success.main' : 'error.main',
              }}
            >
              {posChange > 0 ? (
                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: 18 }} />
              )}
              <Typography variant="body2" fontWeight={500}>
                {Math.abs(posChange)}
              </Typography>
            </Box>
          )}
        </Box>
        {/* Period selector — right-aligned */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {[30, 60, 90].map((d) => (
            <Button
              key={d}
              size="small"
              variant={days === d ? 'contained' : 'outlined'}
              onClick={() => onDaysChange(d)}
              sx={
                days === d
                  ? {
                      backgroundColor: '#054AD9',
                      '&:hover': { backgroundColor: '#043eaf' },
                      textTransform: 'none',
                    }
                  : { textTransform: 'none' }
              }
            >
              {d}d
            </Button>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {keyword.searchEngine} &middot; {keyword.location.toUpperCase()} &middot; Tracked since{' '}
          {new Date(keyword.createdAt).toLocaleDateString()}
        </Typography>

        {/* Copilot control */}
        {!copilotSession ? (
          <Button
            size="small"
            variant="outlined"
            onClick={onActivateCopilot}
            startIcon={
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#054AD9',
                  display: 'inline-block',
                }}
              />
            }
            sx={{ textTransform: 'none', borderColor: '#054AD9', color: '#054AD9' }}
          >
            Activate Copilot
          </Button>
        ) : copilotSession.status === 'active' ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 0.75,
              border: '2px solid #054AD920',
              borderRadius: '8px',
              backgroundColor: '#054AD908',
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#054AD9',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
              }}
            />
            <Typography variant="caption" fontWeight={500} color="#054AD9">
              Copilot active — cycle {copilotSession.totalCycles}
            </Typography>
            <Button
              size="small"
              onClick={onPauseCopilot}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                minWidth: 'auto',
                p: '2px 8px',
                color: '#71717A',
              }}
            >
              Pause
            </Button>
          </Box>
        ) : copilotSession.status === 'paused' ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 0.75,
              border: '2px solid #F4B40030',
              borderRadius: '8px',
              backgroundColor: '#F4B40008',
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#F4B400' }} />
            <Typography variant="caption" fontWeight={500} color="#F4B400">
              Copilot paused
            </Typography>
            <Button
              size="small"
              onClick={onResumeCopilot}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                minWidth: 'auto',
                p: '2px 8px',
                color: '#054AD9',
              }}
            >
              Resume
            </Button>
          </Box>
        ) : copilotSession.status === 'completed' ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              border: '2px solid #0D9D5830',
              borderRadius: '8px',
              backgroundColor: '#0D9D5808',
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#0D9D58' }} />
            <Typography variant="caption" fontWeight={500} color="#0D9D58">
              Goal reached
            </Typography>
          </Box>
        ) : null}
      </Box>

      {/* Key metrics row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        {/* Live Position — real-time from SerpAPI */}
        <Box
          sx={{
            ...cardSx,
            border: '2px solid',
            borderColor:
              livePosition?.livePosition != null
                ? positionColor(livePosition.livePosition) + '40'
                : 'divider',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            Live Position
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: livePositionLoading ? '#F4B400' : '#0D9D58',
              }}
            />
          </Typography>
          {livePositionLoading ? (
            <CircularProgress size={24} sx={{ color: '#054AD9', mt: 0.5 }} />
          ) : (
            <Typography
              variant="h4"
              fontWeight={700}
              color={positionColor(livePosition?.livePosition)}
            >
              {livePosition?.livePosition != null
                ? `#${livePosition.livePosition}`
                : 'Not in top 20'}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, mt: 0.25 }}>
            {livePosition?.checkedAt
              ? `Checked ${new Date(livePosition.checkedAt).toLocaleTimeString()}`
              : 'via Google'}
          </Typography>
        </Box>

        {/* GSC Avg Position */}
        <Box sx={cardSx}>
          <Typography variant="caption" color="text.secondary">
            Avg. Position ({days}d)
          </Typography>
          <Typography variant="h4" fontWeight={700} color="#F4B400">
            {totals ? `#${totals.avgPosition.toFixed(1)}` : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, mt: 0.25 }}>
            GSC average
          </Typography>
        </Box>

        <Box sx={cardSx}>
          <Typography variant="caption" color="text.secondary">
            Clicks ({days}d)
          </Typography>
          <Typography variant="h4" fontWeight={700} color="#4285F4">
            {totals ? fmtNum(totals.clicks) : '—'}
          </Typography>
        </Box>
        <Box sx={cardSx}>
          <Typography variant="caption" color="text.secondary">
            Impressions ({days}d)
          </Typography>
          <Typography variant="h4" fontWeight={700} color="#A142F4">
            {totals ? fmtNum(totals.impressions) : '—'}
          </Typography>
        </Box>
        <Box sx={cardSx}>
          <Typography variant="caption" color="text.secondary">
            CTR
          </Typography>
          <Typography variant="h4" fontWeight={700} color="#0D9D58">
            {totals ? `${(totals.avgCtr * 100).toFixed(1)}%` : '—'}
          </Typography>
        </Box>

        {/* Keyword Difficulty (Ahrefs) */}
        <Box
          sx={{
            ...cardSx,
            border: ahrefsKd?.difficulty != null ? '2px solid' : undefined,
            borderColor:
              ahrefsKd?.difficulty != null
                ? ahrefsKd.difficulty < 30
                  ? '#0D9D5840'
                  : ahrefsKd.difficulty < 60
                  ? '#F4B40040'
                  : '#EA433540'
                : 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            KD
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={
              ahrefsKd?.difficulty != null
                ? ahrefsKd.difficulty < 30
                  ? '#0D9D58'
                  : ahrefsKd.difficulty < 60
                  ? '#F4B400'
                  : '#EA4335'
                : '#96979b'
            }
          >
            {ahrefsKd?.difficulty != null ? ahrefsKd.difficulty : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, mt: 0.25 }}>
            {ahrefsKd?.difficulty != null
              ? ahrefsKd.difficulty < 30
                ? 'Easy'
                : ahrefsKd.difficulty < 60
                ? 'Medium'
                : 'Hard'
              : 'Ahrefs'}
          </Typography>
        </Box>
      </Box>

      {/* Charts row */}
      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 4 }}
      >
        {/* Position history with change markers */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 3,
            backgroundColor: '#fff',
          }}
        >
          <Typography
            variant="overline"
            fontWeight={700}
            color="text.secondary"
            sx={{ mb: 2, display: 'block', letterSpacing: 1.5 }}
          >
            Position History
          </Typography>
          <KeywordHistoryChart
            snapshots={snapshots}
            appliedChanges={appliedChanges}
            metric="position"
            height={200}
          />
        </Box>

        {/* Clicks trend with change markers */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 3,
            backgroundColor: '#fff',
          }}
        >
          <Typography
            variant="overline"
            fontWeight={700}
            color="text.secondary"
            sx={{ mb: 2, display: 'block', letterSpacing: 1.5 }}
          >
            Daily Clicks
          </Typography>
          {isLoadingGSC ? (
            <Box
              sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <CircularProgress size={20} sx={{ color: '#4285F4' }} />
            </Box>
          ) : (
            <KeywordHistoryChart
              snapshots={snapshots}
              appliedChanges={appliedChanges}
              metric="clicks"
              height={200}
            />
          )}
        </Box>
      </Box>

      {/* Top 3 Competitors */}
      {serpAnalysis?.competitors && serpAnalysis.competitors.length > 0 && (
        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <GroupsIcon sx={{ fontSize: 20, color: '#EA4335' }} />
            <Typography
              variant="overline"
              fontWeight={700}
              color="text.secondary"
              sx={{ letterSpacing: 1.5 }}
            >
              Top 3 Google Competitors
            </Typography>
            <Tooltip title="Re-analyze Google Competitors">
              <IconButton
                size="small"
                onClick={() => reanalyzeRef.current?.()}
                sx={{ ml: 'auto', p: 0.5 }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            External sites ranking on Google's first page for "{keyword.keyword}".
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {serpAnalysis.competitors.slice(0, 3).map((comp, i) => {
              const medalColors = ['#F4B400', '#96979b', '#CD7F32'];
              let domain = '';
              try {
                domain = new URL(comp.url).hostname.replace(/^www\./, '');
              } catch {
                domain = comp.url;
              }
              return (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    minWidth: 260,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: i === 0 ? '#EA433530' : 'divider',
                    backgroundColor: i === 0 ? '#FFF5F5' : '#fafafa',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: medalColors[i] ?? '#96979b',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 800,
                      }}
                    >
                      {comp.serpPosition ?? i + 1}
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: 13 }}>
                      {domain}
                    </Typography>
                    <Chip
                      label={`${comp.wordCount?.toLocaleString() ?? '?'} words`}
                      size="small"
                      sx={{ fontSize: 10, height: 18, ml: 'auto' }}
                    />
                  </Box>

                  {/* Title — styled like a Google result */}
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      fontSize: 13,
                      color: '#1a0dab',
                      mb: 0.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {comp.title || 'Untitled'}
                  </Typography>

                  {/* Meta */}
                  {comp.meta && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: 11,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 1,
                      }}
                    >
                      {comp.meta}
                    </Typography>
                  )}

                  {/* URL */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 10,
                        color: '#0D9D58',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={comp.url}
                    >
                      {comp.url}
                    </Typography>
                    <a href={comp.url} target="_blank" rel="noopener noreferrer">
                      <OpenInNewIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                    </a>
                  </Box>

                  {/* Topics */}
                  {comp.topics?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {comp.topics.slice(0, 4).map((t, j) => (
                        <Chip
                          key={j}
                          label={t}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 9, height: 18 }}
                        />
                      ))}
                      {comp.topics.length > 4 && (
                        <Chip
                          label={`+${comp.topics.length - 4}`}
                          size="small"
                          sx={{ fontSize: 9, height: 18 }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Key headings */}
                  {comp.headings?.length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{ fontSize: 10, mb: 0.25, display: 'block' }}
                      >
                        Key Headings
                      </Typography>
                      {comp.headings.slice(0, 4).map((h, j) => (
                        <Typography
                          key={j}
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: 10, display: 'block' }}
                        >
                          {h}
                        </Typography>
                      ))}
                      {comp.headings.length > 4 && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                          +{comp.headings.length - 4} more headings
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Backlink Strength (Ahrefs) */}
      {ahrefsDomainSnapshot && (
        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LinkIcon sx={{ fontSize: 20, color: '#054AD9' }} />
            <Typography
              variant="overline"
              fontWeight={700}
              color="text.secondary"
              sx={{ letterSpacing: 1.5 }}
            >
              Backlink Strength
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontSize: 10 }}>
              via Ahrefs
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Domain Rating */}
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Domain Rating
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#054AD9">
                {ahrefsDomainSnapshot.domainRating ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                / 100
              </Typography>
            </Box>

            {/* Referring Domains */}
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Referring Domains
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#4285F4">
                {ahrefsDomainSnapshot.refdomainsLive != null
                  ? fmtNum(ahrefsDomainSnapshot.refdomainsLive)
                  : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                live
              </Typography>
            </Box>

            {/* Total Backlinks */}
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Backlinks
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#A142F4">
                {ahrefsDomainSnapshot.backlinksLive != null
                  ? fmtNum(ahrefsDomainSnapshot.backlinksLive)
                  : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                {ahrefsDomainSnapshot.dofollowLinks != null
                  ? `${fmtNum(ahrefsDomainSnapshot.dofollowLinks)} dofollow`
                  : 'live'}
              </Typography>
            </Box>

            {/* New Backlinks (7d) */}
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: ahrefsNewBacklinks > 0 ? '#0D9D5830' : 'divider',
                textAlign: 'center',
                backgroundColor: ahrefsNewBacklinks > 0 ? '#F0FFF4' : undefined,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                New <TrendingUpIcon sx={{ fontSize: 12, color: '#0D9D58' }} />
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#0D9D58">
                +{ahrefsNewBacklinks}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                last 7 days
              </Typography>
            </Box>

            {/* Lost Backlinks (7d) */}
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: ahrefsLostBacklinks > 3 ? '#EA433530' : 'divider',
                textAlign: 'center',
                backgroundColor: ahrefsLostBacklinks > 3 ? '#FFF5F5' : undefined,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                Lost <TrendingDownIcon sx={{ fontSize: 12, color: '#EA4335' }} />
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                color={ahrefsLostBacklinks > 3 ? '#EA4335' : '#96979b'}
              >
                {ahrefsLostBacklinks > 0 ? `-${ahrefsLostBacklinks}` : '0'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                last 7 days
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Keyword Opportunities */}
      <KeywordOpportunities
        projectId={projectId}
        keywordId={keywordId}
        keyword={keyword.keyword}
        position={totals?.avgPosition ?? latest?.position ?? null}
        clicks={totals?.clicks ?? null}
        impressions={totals?.impressions ?? null}
        ctr={totals?.avgCtr ?? null}
        days={days}
        rankingPages={rankingPages}
        serpSummary={serpSummary}
        positionHistory={dailyTrend.map((d) => ({ date: d.date, position: d.position }))}
        rankingPageUrl={rankingPageUrl}
        sitePages={sitePages}
        serpAnalysis={serpAnalysis}
        livePosition={livePosition ?? undefined}
        existingTasks={tasks}
        onTaskCreated={onTaskCreated}
      />

      {/* Target Page Selector */}
      <Box sx={sectionSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <LinkIcon sx={{ fontSize: 20, color: '#4285F4' }} />
          <Typography
            variant="overline"
            fontWeight={700}
            color="text.secondary"
            sx={{ letterSpacing: 1.5 }}
          >
            Target Page
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          The page you want to rank for this keyword. All AI suggestions and WordPress updates will
          be applied to this page.
        </Typography>

        {/* Current target display */}
        {rankingPageUrl && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              p: 1.5,
              backgroundColor: '#f0f7ff',
              borderRadius: 2,
              border: '1px solid #4285F420',
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#4285F4',
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ wordBreak: 'break-all', fontSize: 13 }}
            >
              {rankingPageUrl}
            </Typography>
            <a
              href={rankingPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ flexShrink: 0 }}
            >
              <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </a>
            {keyword.targetPageId && (
              <Chip
                label="Confirmed"
                size="small"
                color="success"
                sx={{ fontSize: 9, height: 18, ml: 'auto', flexShrink: 0 }}
              />
            )}
            {!keyword.targetPageId && rankingPageUrl && (
              <>
                <Chip
                  label="Auto-detected"
                  size="small"
                  sx={{
                    fontSize: 9,
                    height: 18,
                    ml: 'auto',
                    flexShrink: 0,
                    color: '#92400E',
                    backgroundColor: '#FEF3C7',
                  }}
                />
                <Chip
                  label="Confirm"
                  size="small"
                  clickable
                  onClick={() => {
                    const match = projectPages.find((p) => p.url === rankingPageUrl);
                    if (match) onTargetPageChange(match.id, match.url);
                  }}
                  sx={{
                    fontSize: 9,
                    height: 18,
                    flexShrink: 0,
                    color: '#fff',
                    backgroundColor: '#054AD9',
                    '&:hover': { backgroundColor: '#043eaf' },
                  }}
                />
              </>
            )}
          </Box>
        )}

        {/* Page selector */}
        <Autocomplete
          options={projectPages}
          getOptionLabel={(option) => `${option.path}${option.title ? ` — ${option.title}` : ''}`}
          value={projectPages.find((p) => p.url === rankingPageUrl) ?? null}
          onChange={(_e, newValue) => {
            if (newValue) {
              onTargetPageChange(newValue.id, newValue.url);
            } else {
              onTargetPageChange(null, '');
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search pages by URL or title..."
              size="small"
              sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 500 }}>
                  {option.path}
                </Typography>
                {option.title && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                    {option.title}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          filterOptions={(options, { inputValue }) => {
            const q = inputValue.toLowerCase();
            return options.filter(
              (o) =>
                o.path.toLowerCase().includes(q) ||
                o.url.toLowerCase().includes(q) ||
                (o.title ?? '').toLowerCase().includes(q)
            );
          }}
          noOptionsText="No pages found. Pages are synced from Google Search Console."
          size="small"
          clearOnBlur
          blurOnSelect
        />
      </Box>

      {/* Copilot Activity */}
      {copilotSession && (
        <Box
          sx={{
            border: '2px solid #054AD920',
            borderRadius: 1.5,
            p: 3,
            backgroundColor: '#054AD904',
            mb: 4,
          }}
        >
          {/* Header */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor:
                    copilotSession.status === 'active'
                      ? '#054AD9'
                      : copilotSession.status === 'completed'
                      ? '#0D9D58'
                      : '#F4B400',
                  ...(copilotSession.status === 'active'
                    ? {
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                      }
                    : {}),
                }}
              />
              <Typography
                variant="overline"
                fontWeight={500}
                color="#054AD9"
                sx={{ letterSpacing: 0.5 }}
              >
                Copilot Activity
              </Typography>
              <Chip
                label={`${copilotSession.status} — cycle ${copilotSession.totalCycles}/${copilotSession.maxCycles}`}
                size="small"
                sx={{ fontSize: 10, height: 18, textTransform: 'capitalize' }}
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={onRerunCopilotAnalysis}
              disabled={isRerunning}
              startIcon={
                isRerunning ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />
              }
              sx={{ textTransform: 'none', borderColor: '#054AD9', color: '#054AD9', fontSize: 11 }}
            >
              {isRerunning ? 'Running...' : 'Run Cycle Now'}
            </Button>
          </Box>

          {/* Session summary metrics */}
          <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                Target
              </Typography>
              <Typography variant="body2" fontWeight={700} color="#054AD9">
                Top {copilotSession.targetPosition}
              </Typography>
            </Box>
            {copilotSession.startingPosition != null && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  Started at
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  #{copilotSession.startingPosition.toFixed(1)}
                </Typography>
              </Box>
            )}
            {copilotSession.currentPosition != null && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  Current
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color={positionColor(copilotSession.currentPosition)}
                >
                  #{copilotSession.currentPosition.toFixed(1)}
                </Typography>
              </Box>
            )}
            {copilotSession.bestPosition != null && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  Best
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#0D9D58">
                  #{copilotSession.bestPosition.toFixed(1)}
                </Typography>
              </Box>
            )}
            {copilotSession.startingPosition != null && copilotSession.currentPosition != null && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  Progress
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color={
                    copilotSession.startingPosition - copilotSession.currentPosition > 0
                      ? '#0D9D58'
                      : '#EA4335'
                  }
                >
                  {copilotSession.startingPosition - copilotSession.currentPosition > 0 ? '+' : ''}
                  {(copilotSession.startingPosition - copilotSession.currentPosition).toFixed(
                    1
                  )}{' '}
                  positions
                </Typography>
              </Box>
            )}
            {copilotSession.nextCycleAt && copilotSession.status === 'active' && (
              <Box sx={{ ml: 'auto' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  Next cycle
                </Typography>
                <Typography variant="body2" fontWeight={500} color="text.secondary">
                  {new Date(copilotSession.nextCycleAt).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Task summary */}
          {(() => {
            const copilotTasks = tasks.filter((t) => t.title.startsWith('[Copilot]'));
            const doneTasks = copilotTasks.filter((t) => t.status === 'done');
            const todoTasks = copilotTasks.filter((t) => t.status === 'todo');
            const inProgressTasks = copilotTasks.filter((t) => t.status === 'in_progress');

            return (
              <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>{doneTasks.length}</strong> done
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <strong>{inProgressTasks.length}</strong> in progress
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <strong>{todoTasks.length}</strong> pending
                </Typography>
              </Box>
            );
          })()}

          {/* Cycle History */}
          {copilotCycles.length > 0 &&
            (() => {
              const copilotTasks = tasks.filter((t) => t.title.startsWith('[Copilot]'));
              const sortedCycles = [...copilotCycles].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              );

              // Match applied changes to cycles by timestamp
              // Changes are applied during a cycle, before the cycle record is finalized
              const chronoCycles = [...copilotCycles].sort(
                (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
              );
              function getChangesForCycle(cycle: SeoCopilotCycle): WpAppliedChange[] {
                const idx = chronoCycles.findIndex((c) => c.id === cycle.id);
                const prevEnd = idx > 0 ? new Date(chronoCycles[idx - 1].createdAt).getTime() : 0;
                const cycleEnd = new Date(cycle.createdAt).getTime();
                return appliedChanges.filter((c) => {
                  const t = new Date(c.appliedAt).getTime();
                  return t > prevEnd && t <= cycleEnd + 5000;
                });
              }

              return (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: '#054AD920' }}>
                  <Typography
                    variant="overline"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ letterSpacing: 1, mb: 1.5, display: 'block', fontSize: 10 }}
                  >
                    Cycle History
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {sortedCycles.map((cycle) => {
                      const cycleTasks = copilotTasks.filter((t) => t.cycleId === cycle.id);
                      const cycleChanges = getChangesForCycle(cycle);
                      const impactColor =
                        cycle.impactStatus === 'improved'
                          ? '#0D9D58'
                          : cycle.impactStatus === 'declined'
                          ? '#EA4335'
                          : '#71717A';
                      const posGain =
                        cycle.positionBefore != null && cycle.positionAfter != null
                          ? cycle.positionBefore - cycle.positionAfter
                          : null;

                      return (
                        <CycleCard
                          key={cycle.id}
                          cycle={cycle}
                          cycleTasks={cycleTasks}
                          cycleChanges={cycleChanges}
                          impactColor={impactColor}
                          posGain={posGain}
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}
        </Box>
      )}

      {/* Tasks */}
      <KeywordTaskList
        tasks={tasks}
        isLoading={tasksLoading}
        onStatusChange={onTaskStatusChange}
        onApplyToWp={onTaskApplyToWp}
        wpConnected={wpConnected}
      />

      {/* All ranking pages for this keyword */}
      {rankingPages.length > 0 && (
        <Box sx={sectionSx}>
          <Typography
            variant="overline"
            fontWeight={700}
            color="text.secondary"
            sx={{ mb: 2, display: 'block', letterSpacing: 1.5 }}
          >
            All Pages Ranking for "{keyword.keyword}"
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {rankingPages.length > 1
              ? `${rankingPages.length} pages are competing for this keyword. Consider consolidating to avoid keyword cannibalization.`
              : 'Only 1 page is ranking — no cannibalization issues.'}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell align="center">Position</TableCell>
                  <TableCell align="center">Clicks</TableCell>
                  <TableCell align="center">Impressions</TableCell>
                  <TableCell align="center">CTR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rankingPages.map((pg, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 350,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={pg.page}
                        >
                          {formatPath(pg.page)}
                        </Typography>
                        <a href={pg.page} target="_blank" rel="noopener noreferrer">
                          <OpenInNewIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        </a>
                        {i === 0 && (
                          <Chip
                            label="Primary"
                            size="small"
                            color="success"
                            sx={{ fontSize: 10, height: 18, ml: 0.5 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`#${pg.position.toFixed(1)}`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: 11,
                          color: '#fff',
                          backgroundColor: positionColor(pg.position),
                          minWidth: 44,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">{pg.clicks}</TableCell>
                    <TableCell align="center">{fmtNum(pg.impressions)}</TableCell>
                    <TableCell align="center">{(pg.ctr * 100).toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Internal Link Finder */}
      <Box sx={sectionSx}>
        <Typography
          variant="overline"
          fontWeight={700}
          color="text.secondary"
          sx={{ mb: 1, display: 'block', letterSpacing: 1.5 }}
        >
          Internal Link Opportunities
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Find pages on your site that should link to this keyword's ranking page.
        </Typography>
        <InternalLinkFinder
          keyword={keyword.keyword}
          keywordId={keywordId}
          projectId={projectId}
          targetPage={rankingPageUrl}
          sitePages={sitePages}
          cachedResult={internalLinks}
        />
      </Box>

      {/* Keyword info */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 3,
          backgroundColor: '#fff',
        }}
      >
        <Typography
          variant="overline"
          fontWeight={700}
          color="text.secondary"
          sx={{ mb: 2, display: 'block', letterSpacing: 1.5 }}
        >
          Keyword Info
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Keyword
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {keyword.keyword}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Search Engine
          </Typography>
          <Typography variant="body2">{keyword.searchEngine}</Typography>
          <Typography variant="caption" color="text.secondary">
            Location
          </Typography>
          <Typography variant="body2">{keyword.location.toUpperCase()}</Typography>
          <Typography variant="caption" color="text.secondary">
            Tracking Since
          </Typography>
          <Typography variant="body2">
            {new Date(keyword.createdAt).toLocaleDateString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Data Points
          </Typography>
          <Typography variant="body2">{snapshots.length} snapshots</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SeoKeywordDetailComponent;
